/**
 * Klick-Menue am Wert (D-14): Wert, Vorjahr, Regelergebnis, Zustand, Sperren/Freigeben,
 * "Wieder nach Regel", optionale Notiz und Link zur Aufschluesselung -- kein Sperrmodus, kein
 * Schloss-Hover. Baut auf demselben `Popover`-Bedienmuster wie `AlternativeFuelCategoryMenu.tsx`
 * (Klick statt Hover, `withinPortal`) und dem `@mantine/form`-Notizfeld-Muster aus
 * `AssignmentCascade.tsx` (Projektregel CLAUDE.md: Feldfehler ausschliesslich vom Backend).
 *
 * Aggregate (Compliance, Zustand, Rueckrechenbarkeit) kommen fertig aus `CellInfo` -- diese
 * Komponente rechnet nichts nach, sie zeigt nur an und meldet Aenderungen an den Server.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  Button,
  Group,
  Popover,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
// Umbenannt importiert -- die Verwendung liest sich unter dem spezifischeren Namen klarer, und
// der Mantine-Hook selbst wird dadurch nur an einer einzigen Stelle dieser Datei genannt.
import { useForm as useNoteForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconLock, IconLockOpen } from '@tabler/icons-react';
import { evaluationTableApi } from '../../services/evaluationTableApi';
import { vdzOrange } from '../../theme/colors';
import {
  getApiErrorMessage,
  getApiViolations,
} from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import { formatShare, formatTonnes } from './evaluationFormat';
import type { EvaluationCellUnit } from './EvaluationValueCell';
import type { EvaluationType } from '../../types/evaluations';
import { EvaluationTypeLabels } from '../../types/evaluations';
import type { CellInfo, CellState } from '../../types/evaluationTable';
import classes from './evaluationTable.module.css';

/* 39-12: die beiden Klassen dieser Datei (--eval-recoverable-hint-*) leben im gemeinsamen
   CSS-Modul aller Tabellenbausteine dieser Phase, Farben kommen weiterhin ausschliesslich aus der
   Palette -- keine neuen Hex-Werte. Ersetzt die 39-10-Inline-Styles (Deviation 2 in 39-10-SUMMARY.md),
   die entstanden, weil evaluationTable.module.css damals ausserhalb des Task-Datei-Scopes lag. */
const recoverableHintColorVars = {
  '--eval-recoverable-hint-bg': vdzOrange[1],
  '--eval-recoverable-hint-fg': vdzOrange[7],
} as CSSProperties;

const STATE_LABELS: Record<CellState, string> = {
  free: 'Frei',
  locked_rule: 'Gesperrt nach Regel',
  locked_manual: 'Von Hand gesperrt',
  released_manual: 'Trotz Regel freigegeben',
};

function formattedValue(
  value: string | null,
  unit: EvaluationCellUnit
): string {
  return unit === 'percent' ? formatShare(value) : `${formatTonnes(value)} t`;
}

function ruleText(cell: CellInfo): string {
  if (!cell.compliance) {
    return 'Importierter Zeitraum – keine Regelprüfung';
  }
  if (!cell.compliance.ok) {
    return `Regel sperrt: ${cell.compliance.problems.join('; ')}`;
  }
  return `Regel erfüllt: ${cell.compliance.companies} Unternehmen, größter Anteil ${formatShare(cell.compliance.maxShare)}`;
}

function toggleNotificationText(label: string, state: CellState): string {
  switch (state) {
    case 'locked_manual':
      return `${label}: von Hand gesperrt`;
    case 'released_manual':
      return `${label}: trotz Regel freigegeben`;
    case 'free':
      return `${label}: wieder nach Regel`;
    default:
      return `${label}: gesperrt nach Regel`;
  }
}

interface NoteFieldProps {
  cell: CellInfo;
  evaluationType: EvaluationType;
  year: number;
  period: number;
  onChanged: () => void;
}

function NoteField({
  cell,
  evaluationType,
  year,
  period,
  onChanged,
}: NoteFieldProps) {
  const [saving, setSaving] = useState(false);
  const form = useNoteForm<{ note: string }>({
    mode: 'controlled',
    initialValues: { note: cell.decision?.note ?? '' },
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await evaluationTableApi.setCellDecision({
        evaluationType,
        year,
        period,
        cellKey: cell.cellKey,
        locked: cell.blocked,
        note: form.values.note,
      });
      form.resetDirty();
      onChanged();
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      notifications.show({
        color: 'red',
        title: 'Nicht gespeichert',
        message: getApiErrorMessage(
          err,
          'Die Notiz konnte nicht gespeichert werden.'
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TextInput
        label="Notiz (optional)"
        placeholder="z. B. schützt Saarland"
        {...form.getInputProps('note')}
      />
      <Button
        size="xs"
        disabled={!form.isDirty() || saving}
        loading={saving}
        onClick={() => void handleSave()}
      >
        Notiz speichern
      </Button>
    </>
  );
}

export interface CellLockMenuProps {
  opened: boolean;
  onClose: () => void;
  target: ReactNode;
  cell: CellInfo;
  unit: EvaluationCellUnit;
  evaluationType: EvaluationType;
  year: number;
  period: number;
  editable: boolean;
  onChanged: () => void;
  onOpenBreakdown: (rowKey: string) => void;
}

export function CellLockMenu({
  opened,
  onClose,
  target,
  cell,
  unit,
  evaluationType,
  year,
  period,
  editable,
  onChanged,
  onOpenBreakdown,
}: CellLockMenuProps) {
  const [toggling, setToggling] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const response = await evaluationTableApi.setCellDecision({
        evaluationType,
        year,
        period,
        cellKey: cell.cellKey,
        locked: !cell.blocked,
      });
      notifications.show({
        message: toggleNotificationText(cell.label, response.state),
      });
      onChanged();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Nicht gespeichert',
        message: getApiErrorMessage(
          err,
          'Die Änderung konnte nicht gespeichert werden.'
        ),
      });
    } finally {
      setToggling(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await evaluationTableApi.deleteCellDecision({
        evaluationType,
        year,
        period,
        cellKey: cell.cellKey,
      });
      notifications.show({ message: `${cell.label}: wieder nach Regel` });
      onChanged();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Nicht gespeichert',
        message: getApiErrorMessage(
          err,
          'Die Änderung konnte nicht gespeichert werden.'
        ),
      });
    } finally {
      setResetting(false);
    }
  };

  return (
    <Popover
      opened={opened}
      // Kontrollierter Popover: Mantine loest Escape/Klick-ausserhalb intern nur ueber `onChange`
      // aus (der `onClose`-Callback selbst reagiert erst NACH einer Aenderung von `opened`) --
      // deshalb hier auf `onClose` abbilden, statt `onClose` direkt durchzureichen.
      onChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
      withinPortal
      position="bottom"
      shadow="md"
      width={290}
      closeOnEscape
      closeOnClickOutside
      trapFocus
    >
      <Popover.Target>
        <span className={classes.menuAnchor}>{target}</span>
      </Popover.Target>
      {/* Das Menue haengt im React-Baum unter der Wertzelle: ohne diese Sperre oeffnete ein Klick
          hier (etwa "Aufschluesselung je Werk") das Menue gleich wieder, und das
          preventDefault() der Zelle schluckte das Leerzeichen im Notizfeld. */}
      <Popover.Dropdown
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Stack gap={6}>
          <div>
            <Text fw={700} size="sm">
              {cell.label}
            </Text>
            <Text size="xs" c="dimmed">
              {EvaluationTypeLabels[evaluationType]} {year}
            </Text>
          </div>
          <Group gap="sm" align="baseline">
            <Text fw={700} fz="lg">
              {formattedValue(cell.value, unit)}
            </Text>
            <Text size="xs" c="dimmed">
              Vorjahr {formattedValue(cell.previousValue, unit)}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            {ruleText(cell)}
          </Text>
          <Group gap={6}>
            {cell.blocked ? <IconLock size={16} /> : <IconLockOpen size={16} />}
            <Text fw={700} size="sm">
              {STATE_LABELS[cell.state]}
            </Text>
          </Group>
          {cell.recoverable && (
            <Text
              size="xs"
              className={classes.menuRecoverableHint}
              style={recoverableHintColorVars}
            >
              Lässt sich zurückrechnen: {cell.recoverable.description}
            </Text>
          )}
          {!editable && (
            <Text size="xs" c="dimmed">
              Importierter Zeitraum – die Werte sind endgültig.
            </Text>
          )}
          {editable && (
            <>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant={cell.blocked ? 'light' : 'filled'}
                  loading={toggling}
                  onClick={() => void handleToggle()}
                >
                  {cell.blocked ? 'Freigeben' : 'Sperren'}
                </Button>
                {cell.decision && (
                  <Button
                    size="xs"
                    variant="light"
                    loading={resetting}
                    onClick={() => void handleReset()}
                  >
                    Wieder nach Regel
                  </Button>
                )}
              </Group>
              {cell.decision && (
                <NoteField
                  key={cell.cellKey}
                  cell={cell}
                  evaluationType={evaluationType}
                  year={year}
                  period={period}
                  onChanged={onChanged}
                />
              )}
            </>
          )}
          {cell.breakdownRowKey && (
            <UnstyledButton
              className={classes.menuLink}
              onClick={() => onOpenBreakdown(cell.breakdownRowKey as string)}
            >
              Aufschlüsselung je Werk
            </UnstyledButton>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
